import { useState, useMemo } from 'react';
import { Row, Col, Card, Form, Button, Badge, Spinner, Alert, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRotate,
  faTriangleExclamation,
  faFlask,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import PageBreadcrumb from '../../components/common/PageBreadcrumb';
import { useProductionPlan } from '../../features/planning/useProductionPlan';
import PlanSources from '../../components/planning/PlanSources';

const breadcrumbItems = [
  { label: 'Project Management', url: '/apps/project-management/project-monitor' },
  { label: 'Production Planner', active: true }
];

const dur = sec => {
  const s = Math.round(Number(sec) || 0);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  const h = s / 3600;
  return h < 24 ? `${h.toFixed(1)}h` : `${(h / 24).toFixed(1)}d`;
};

const when = ms => (ms ? dayjs(ms).format('DD MMM HH:mm') : '—');
const m = v => `${Math.round(Number(v) || 0).toLocaleString()} m`;

const CHANGEOVER_TONE = {
  MATERIAL_CHANGE: 'danger',
  PHASE_CHANGEOVER: 'warning',
  PRODUCT_CHANGEOVER: 'warning',
  COLOR_CHANGE: 'info',
  DRUM_CHANGE: 'secondary',
  COLD_START: 'secondary'
};

const Kpi = ({ title, value, sub, tone = 'body-emphasis' }) => (
  <Card className="h-100">
    <Card.Body className="py-3">
      <p className="text-body-tertiary fs-10 mb-1">{title}</p>
      <h4 className={`mb-0 text-${tone}`}>{value}</h4>
      {sub ? <p className="text-body-tertiary fs-10 mb-0 mt-1">{sub}</p> : null}
    </Card.Body>
  </Card>
);

/**
 * What the factory will do, and when it will be done.
 *
 * Read-only by design: nothing on this page writes a queue or touches a job. It
 * asks the planner what the current state implies and shows the answer, so it
 * can be opened, poked at and closed without consequence.
 *
 * The numbers are deliberately not merged. "Work" is how long a machine is
 * busy; "lead time" is how long until the cable actually exists, including
 * every hour it spends waiting its turn. A cable with six hours of work can be
 * two days away, and showing only the six would be the most misleading thing
 * this page could do.
 */
const ProductionPlanner = () => {
  const [project, setProject] = useState(true);
  const [queues, setQueues] = useState(true);
  const [includeLive, setIncludeLive] = useState(true);
  const [assumeSpeed, setAssumeSpeed] = useState(false);
  const [items, setItems] = useState([]);

  const addItem = item =>
    setItems(list => (list.some(i => i.key === item.key) ? list : [...list, item]));
  const removeItem = key => setItems(list => list.filter(i => i.key !== key));
  const setLength = (key, lengthM) =>
    setItems(list => list.map(i => (i.key === key ? { ...i, lengthM } : i)));

  const { data, isLoading, isFetching, refetch } = useProductionPlan({
    items,
    project,
    queues,
    includeLive,
    fallbackSpeed: assumeSpeed ? 100 : null
  });

  const summary = data?.summary;
  const coverage = data?.coverage;
  const steps = useMemo(() => data?.plan?.steps || [], [data]);

  const products = useMemo(
    () =>
      Object.entries(summary?.byProduct || {}).sort(
        (a, b) => (b[1].leadTimeSec || 0) - (a[1].leadTimeSec || 0)
      ),
    [summary]
  );

  const machines = useMemo(
    () => Object.entries(summary?.byMachine || {}).sort((a, b) => b[1].workSec - a[1].workSec),
    [summary]
  );

  const missing = coverage?.missingSpeed || [];

  return (
    <div className="mb-9">
      <PageBreadcrumb items={breadcrumbItems} />

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
        <div>
          <h2 className="mb-1">Production Planner</h2>
          <p className="text-body-tertiary mb-0 fs-9">
            What the factory will do with what it has now. Nothing here is saved
            or launched — close it and nothing has changed.
          </p>
        </div>
        <Button variant="phoenix-secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <FontAwesomeIcon icon={faRotate} className="me-2" spin={isFetching} />
          Recalculate
        </Button>
      </div>

      <PlanSources
        items={items}
        onAdd={addItem}
        onRemove={removeItem}
        onClear={() => setItems([])}
        onChangeLength={setLength}
      />

      <Card className="mb-4">
        <Card.Body className="py-3">
          <Row className="g-4 align-items-center">
            <Col xs="auto">
              <Form.Check
                type="switch"
                id="plan-live"
                label="On top of what is running"
                checked={includeLive}
                onChange={e => setIncludeLive(e.target.checked)}
              />
              <span className="text-body-tertiary fs-10">
                Off = plan the list above against an empty factory
              </span>
            </Col>
            <Col xs="auto">
              <Form.Check
                type="switch"
                id="plan-queues"
                label="Follow the machine queues"
                checked={queues}
                onChange={e => setQueues(e.target.checked)}
              />
              <span className="text-body-tertiary fs-10">
                Run work in the order it was queued, on the machine it was queued to
              </span>
            </Col>
            <Col xs="auto">
              <Form.Check
                type="switch"
                id="plan-project"
                label="Include phases not launched yet"
                checked={project}
                onChange={e => setProject(e.target.checked)}
              />
              <span className="text-body-tertiary fs-10">
                Estimate the rest of each product from its phase plan
              </span>
            </Col>
            <Col xs="auto">
              <Form.Check
                type="switch"
                id="plan-assume"
                label="Assume 100 m/min where unmeasured"
                checked={assumeSpeed}
                onChange={e => setAssumeSpeed(e.target.checked)}
              />
              <span className="text-body-tertiary fs-10">
                For a what-if. Every date below then rests on a guess.
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {isLoading ? (
        <div className="d-flex align-items-center justify-content-center py-8 gap-2">
          <Spinner animation="border" size="sm" variant="primary" />
          <span className="fs-9 text-body-tertiary">Planning…</span>
        </div>
      ) : (
        <>
          {coverage?.usedFallbackSpeed && (
            <Alert variant="warning" className="d-flex align-items-start gap-2 py-2">
              <FontAwesomeIcon icon={faFlask} className="mt-1" />
              <div className="fs-9">
                <b>Illustrative.</b> Machines with no measured speed are running at
                an assumed 100 m/min, so these dates are a what-if rather than a
                forecast.
              </div>
            </Alert>
          )}

          {!!missing.length && (
            <Alert variant="info" className="py-2">
              <div className="fs-9">
                <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
                <b>{missing.length} machine/phase {missing.length === 1 ? 'combination has' : 'combinations have'} never been measured</b>
                {' '}— running each once is all it takes to date this work for real:
                <div className="mt-1">
                  {missing.map(x => (
                    <Badge bg="secondary" className="me-1 fs-10" key={x.key}>
                      {x.key.replace('|', ' · ')} ({x.blocks})
                    </Badge>
                  ))}
                </div>
              </div>
            </Alert>
          )}

          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <Kpi
                title="Drums planned"
                value={summary?.total?.drums ?? 0}
                sub={`${coverage?.projectedSteps ?? 0} not launched yet`}
              />
            </Col>
            <Col xs={6} md={3}>
              <Kpi title="Length" value={m(summary?.total?.meters)} sub={`${coverage?.jobs ?? 0} projects`} />
            </Col>
            <Col xs={6} md={3}>
              <Kpi
                title="Machine time"
                value={dur(summary?.total?.workSec)}
                sub={`${dur(summary?.total?.setupSec)} of it set-up`}
              />
            </Col>
            <Col xs={6} md={3}>
              <Kpi
                title="Everything done in"
                value={dur(summary?.total?.leadTimeSec)}
                sub={when(summary?.total?.endAt)}
                tone="primary"
              />
            </Col>
          </Row>

          <Row className="g-3">
            <Col xs={12} xl={7}>
              <Card className="h-100">
                <Card.Header className="py-2">
                  <h6 className="mb-0">By product</h6>
                  <p className="mb-0 fs-10 text-body-tertiary">
                    <b>Lead time</b> is from now until it exists — machine time plus
                    every hour spent waiting for a line.
                  </p>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table size="sm" className="fs-9 mb-0">
                    <thead>
                      <tr>
                        <th className="ps-3">Product</th>
                        <th className="text-end">Drums</th>
                        <th className="text-end">Length</th>
                        <th className="text-end">Work</th>
                        <th className="text-end">Waiting</th>
                        <th className="text-end pe-3">Ready in</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!products.length ? (
                        <tr>
                          <td colSpan={6} className="text-center py-5 text-body-tertiary">
                            Nothing to plan.
                          </td>
                        </tr>
                      ) : (
                        products.map(([name, b]) => (
                          <tr key={name}>
                            <td className="ps-3 fw-semibold">{name}</td>
                            <td className="text-end">{b.drums}</td>
                            <td className="text-end">{m(b.meters)}</td>
                            <td className="text-end text-body-tertiary">{dur(b.workSec)}</td>
                            <td className="text-end text-body-tertiary">
                              {b.queuedSec ? dur(b.queuedSec) : '—'}
                            </td>
                            <td className="text-end pe-3 fw-bold">{dur(b.leadTimeSec)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} xl={5}>
              <Card className="h-100">
                <Card.Header className="py-2">
                  <h6 className="mb-0">By machine</h6>
                  <p className="mb-0 fs-10 text-body-tertiary">
                    Capacity is how many drums the line runs at once — not how many
                    take-ups it has.
                  </p>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table size="sm" className="fs-9 mb-0">
                    <thead>
                      <tr>
                        <th className="ps-3">Machine</th>
                        <th className="text-end">Drums</th>
                        <th className="text-end">Work</th>
                        <th className="text-end pe-3">Free from</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!machines.length ? (
                        <tr>
                          <td colSpan={4} className="text-center py-5 text-body-tertiary">
                            No machine is loaded.
                          </td>
                        </tr>
                      ) : (
                        machines.map(([id, b]) => (
                          <tr key={id}>
                            <td className="ps-3 text-uppercase fw-semibold">{id}</td>
                            <td className="text-end">{b.drums}</td>
                            <td className="text-end">{dur(b.workSec)}</td>
                            <td className="text-end pe-3 text-body-tertiary">{when(b.endAt)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="mt-3">
            <Card.Header className="py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h6 className="mb-0">The sequence</h6>
                <p className="mb-0 fs-10 text-body-tertiary">
                  In the order it will happen. Amber means the line stands idle
                  waiting for upstream work.
                </p>
              </div>
              <div className="d-flex gap-1 flex-wrap">
                {Object.entries(summary?.changeovers || {}).map(([reason, c]) => (
                  <Badge bg={CHANGEOVER_TONE[reason] || 'secondary'} className="fs-10" key={reason}>
                    {reason.replaceAll('_', ' ').toLowerCase()} ×{c.count} · {dur(c.sec)}
                  </Badge>
                ))}
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table size="sm" className="fs-9 mb-0">
                  <thead>
                    <tr>
                      <th className="ps-3">Start</th>
                      <th>Machine</th>
                      <th>Phase</th>
                      <th>Product</th>
                      <th className="text-end">Length</th>
                      <th className="text-end">Set-up</th>
                      <th className="text-end">Run</th>
                      <th className="pe-3">Ends</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!steps.length ? (
                      <tr>
                        <td colSpan={8} className="text-center py-5 text-body-tertiary">
                          Nothing could be scheduled. Check the gaps above.
                        </td>
                      </tr>
                    ) : (
                      steps.map(s => (
                        <tr key={s.key} className={s.idleWaitingUpstreamSec > 0 ? 'table-warning' : undefined}>
                          <td className="ps-3 text-nowrap">{when(s.startAt)}</td>
                          <td className="text-uppercase">
                            {s.machineId}
                            {s.queued ? (
                              <Badge bg="success" className="ms-1 fs-10">
                                #{s.queueIndex + 1}
                              </Badge>
                            ) : null}
                          </td>
                          <td>
                            {String(s.phase || '').replaceAll('_', ' ')}
                            {s.projected ? (
                              <Badge bg="info" className="ms-1 fs-10" title="Not launched yet — estimated from the phase plan">
                                est
                              </Badge>
                            ) : null}
                          </td>
                          <td className="text-body-tertiary">{s.productLabel}</td>
                          <td className="text-end">{m(s.remainingM)}</td>
                          <td className="text-end text-body-tertiary" title={s.changeover}>
                            {s.setupSec ? dur(s.setupSec) : '—'}
                          </td>
                          <td className="text-end">{dur(s.runSec)}</td>
                          <td className="pe-3 text-nowrap text-body-tertiary">
                            {when(s.endAt)}
                            {s.idleWaitingUpstreamSec > 0 ? (
                              <span className="ms-2 text-warning" title="The line waits for upstream work">
                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                {dur(s.idleWaitingUpstreamSec)}
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

export default ProductionPlanner;
